import { apiClient, unwrap } from '@/lib/apiClient';
import type { ApiSuccess, PaginationMeta } from '@/types/api';
import type { ExportFieldOption, ExportRows } from '@/types/export';
import type {
  StaffImportResult,
  StaffListParams,
  StaffMember,
  StaffSummary,
} from '@/types/staff';
import type { StaffPayload } from '../schemas/staff.schema';

/**
 * Every call the staff screen makes.
 *
 * As with the rest of the institute area there is no institute id in any path — the
 * server takes it from the caller's token, so there is nothing for a client to
 * point at another institute's people.
 */

/**
 * When each photograph was last replaced in this tab, by staff uuid.
 *
 * The photo route answers with `Cache-Control: private, max-age=300` and its URL
 * never changes, so without this the browser keeps serving the old face for
 * five minutes after an upload — and the register reads as if the upload failed.
 * A changed query string is a different URL to the cache; the server ignores it.
 */
const photoVersions = new Map<string, number>();

/** A page of staff, plus the envelope the table's pagination needs. */
export interface StaffListResult {
  items: StaffMember[];
  meta: PaginationMeta;
}

/**
 * Creating never sends a status — a new account is always active. Sending one would let a client open an account that is switched
 * off, which reads as a bug rather than a decision.
 */
export type CreateStaffPayload = Omit<StaffPayload, 'status'> & { password?: string };

export const staffApi = {
  list: async (params: StaffListParams): Promise<StaffListResult> => {
    // Blank filters are dropped so the URL stays readable and the server does
    // not have to treat "" as "all".
    const query = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '' && value !== undefined),
    );

    const response = await apiClient.get<ApiSuccess<StaffMember[]>>('/institute/staff', {
      params: query,
    });

    return {
      items: response.data.data,
      meta:
        response.data.meta ??
        { page: 1, limit: 20, total: response.data.data.length, totalPages: 1 },
    };
  },

  /** The figures above the list, counted across every staff row, not just a page. */
  summary: () => unwrap(apiClient.get<ApiSuccess<StaffSummary>>('/institute/staff/summary')),

  get: (uuid: string) => unwrap(apiClient.get<ApiSuccess<StaffMember>>(`/institute/staff/${uuid}`)),

  create: (payload: CreateStaffPayload) =>
    unwrap(apiClient.post<ApiSuccess<StaffMember>>('/institute/staff', payload)),

  update: (uuid: string, payload: StaffPayload) =>
    unwrap(apiClient.patch<ApiSuccess<StaffMember>>(`/institute/staff/${uuid}`, payload)),

  remove: (uuid: string) => unwrap(apiClient.delete<ApiSuccess<null>>(`/institute/staff/${uuid}`)),

  /** Private, like every upload — needs the access token, so use SecureImage. */
  photoPath: (uuid: string) => {
    const version = photoVersions.get(uuid);
    return `/institute/staff/${uuid}/photo${version ? `?v=${version}` : ''}`;
  },

  /**
   * Replaces a member of staff's photograph.
   *
   * Its own call rather than a field on `update`, because it carries bytes: the
   * patch is a JSON body the server validates with zod, and mixing a file into
   * it would turn every save on the form into a multipart request.
   */
  uploadPhoto: (uuid: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    // Content-Type is left unset on purpose: the browser must add the
    // multipart boundary itself.
    return unwrap(
      apiClient.post<ApiSuccess<StaffMember>>(`/institute/staff/${uuid}/photo`, form, {
        headers: { 'Content-Type': undefined },
      }),
    ).then((member) => {
      photoVersions.set(uuid, Date.now());
      return member;
    });
  },

  /** Takes the photograph down; the record falls back to initials. */
  removePhoto: (uuid: string) =>
    unwrap(apiClient.delete<ApiSuccess<StaffMember>>(`/institute/staff/${uuid}/photo`)).then(
      (member) => {
        photoVersions.set(uuid, Date.now());
        return member;
      },
    ),

  /** A fresh set-password link, invalidating any earlier one. */
  resendInvite: (uuid: string) =>
    unwrap(apiClient.post<ApiSuccess<null>>(`/institute/staff/${uuid}/invite`)),

  /**
   * A password chosen by the administrator, for an existing account. They
   * change it at first sign-in; their sessions and any emailed link end now.
   */
  setPassword: (uuid: string, password: string) =>
    unwrap(apiClient.put<ApiSuccess<null>>(`/institute/staff/${uuid}/password`, { password })),

  /** Where the blank staff template lives. Fetched with the token, like every upload. */
  templatePath: () => '/institute/staff/import/template',

  /**
   * Opening many staff accounts from one spreadsheet.
   *
   * Multipart with a single `sheet` field. The Content-Type is left to the
   * browser so it appends the multipart boundary, which a hand-written header
   * would drop.
   */
  importSheet: (sheet: File) => {
    const form = new FormData();
    form.append('sheet', sheet);

    return unwrap(
      apiClient.post<ApiSuccess<StaffImportResult>>('/institute/staff/import', form, {
        headers: { 'Content-Type': undefined },
      }),
    );
  },

  /** Every detail the download dialog can include, grouped. */
  exportFields: () =>
    unwrap(apiClient.get<ApiSuccess<ExportFieldOption[]>>('/institute/staff/export/fields')),

  /** The filtered directory with the chosen details — every row, audited on the server. */
  exportRows: (filters: StaffExportFilters, fields: string[]) =>
    unwrap(
      apiClient.get<ApiSuccess<ExportRows>>('/institute/staff/export', {
        params: {
          ...Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined),
          ),
          fields: fields.join(','),
        },
      }),
    ),
};

/** What a download can be narrowed by: the directory's filters, without paging. */
export type StaffExportFilters = Omit<StaffListParams, 'page' | 'limit'>;
